import { Link } from 'react-router-dom';
import {
  Stethoscope,
  Scissors,
  Syringe,
  Bug,
  Footprints,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Bell,
} from 'lucide-react';
import { APPOINTMENT_TYPES, ROUTES } from '../../../config/constants';
import { usePets } from '../../../contexts/PetsContext';
import { formatList } from '../../../lib/format';
import Card from '../../../components/ui/Card';

/**
 * Tarjeta visual de una cita, usada en CalendarPage.
 * Link al edit. Resuelve `petIds` → nombres usando PetsContext.
 */

const TYPE_META = {
  [APPOINTMENT_TYPES.VET]:       { icon: Stethoscope, label: 'Veterinario', className: 'text-sky-700 bg-sky-50' },
  [APPOINTMENT_TYPES.GROOMING]:  { icon: Scissors,    label: 'Peluquería',  className: 'text-purple-700 bg-purple-50' },
  [APPOINTMENT_TYPES.VACCINE]:   { icon: Syringe,     label: 'Vacuna',      className: 'text-emerald-700 bg-emerald-50' },
  [APPOINTMENT_TYPES.DEWORMING]: { icon: Bug,         label: 'Antiparasitario', className: 'text-amber-700 bg-amber-50' },
  [APPOINTMENT_TYPES.WALK]:      { icon: Footprints,  label: 'Paseo',       className: 'text-brand-700 bg-brand-50' },
  [APPOINTMENT_TYPES.OTHER]:     { icon: CalendarDays,label: 'Otro',        className: 'text-slate-700 bg-slate-100' },
};

const formatDateTime = (ts) => {
  const ms = ts?.toMillis?.() ?? null;
  if (!ms) return '—';
  const d = new Date(ms);
  // formato "15 de junio, 14:30"
  const date = d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });
  const time = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

const formatRelative = (ts, nowMs = Date.now()) => {
  const ms = ts?.toMillis?.() ?? null;
  if (!ms) return null;
  const diffMs = ms - nowMs;
  const absMin = Math.abs(Math.round(diffMs / 60_000));
  const future = diffMs > 0;

  if (absMin < 60) return future ? `en ${absMin} min` : `hace ${absMin} min`;
  const hours = Math.round(absMin / 60);
  if (hours < 24) return future ? `en ${hours} h` : `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return future ? 'mañana' : 'ayer';
  return future ? `en ${days} días` : `hace ${days} días`;
};

const AppointmentCard = ({ appointment }) => {
  const { pets } = usePets();
  const meta = TYPE_META[appointment.type] ?? TYPE_META[APPOINTMENT_TYPES.OTHER];
  const Icon = meta.icon;

  const petNames = (appointment.petIds || [])
    .map((id) => pets.find((p) => p.id === id)?.name ?? null)
    .filter(Boolean);

  const isCompleted = Boolean(appointment.completedAt);
  const isCancelled = Boolean(appointment.cancelledAt);
  const scheduledMs = appointment.scheduledAt?.toMillis?.() ?? null;
  const isOverdue = !isCompleted && !isCancelled && scheduledMs != null && scheduledMs < Date.now();

  let statusBadge = null;
  if (isCompleted) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Hecha
      </span>
    );
  } else if (isCancelled) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
        <XCircle className="h-3 w-3" /> Cancelada
      </span>
    );
  } else if (isOverdue) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        Vencida
      </span>
    );
  } else if (appointment.reminderMinutes > 0 && !appointment.reminderSentAt) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
        <Bell className="h-3 w-3" /> Recordatorio activo
      </span>
    );
  }

  return (
    <Link
      to={ROUTES.APPOINTMENT_EDIT.replace(':appointmentId', appointment.id)}
      className="block focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-2xl"
    >
      <Card className="hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
          <div className={['flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', meta.className].join(' ')}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className={['font-semibold leading-tight', isCancelled || isCompleted ? 'text-slate-500 line-through' : 'text-slate-900'].join(' ')}>
                {appointment.title}
              </h3>
              {statusBadge}
            </div>
            <p className="mt-0.5 text-sm text-slate-600">
              {formatDateTime(appointment.scheduledAt)}
              {!isCompleted && !isCancelled && scheduledMs && (
                <span className="text-slate-400"> · {formatRelative(appointment.scheduledAt)}</span>
              )}
            </p>
            {petNames.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Para {formatList(petNames)}
              </p>
            )}
            {appointment.notes && (
              <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{appointment.notes}</p>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default AppointmentCard;
